/* screens/analytics/ColumnWidths.jsx — ширина колонок таблиц Аналитики вручную:
   ручка на правой границе заголовка, `<colgroup>` таблицы, замеры и продолжение
   строк до правого края.

   Задача владельца: «для всех универсальных компонентов таблиц добавить
   возможность вручную менять ширину колонок перетаскиванием их границ — и с
   закреплёнными слева столбцами». Модель и чистые правила — в
   lib/table/columnWidths.js; здесь DOM и React.

   ПОДКЛЮЧЕНИЕ к таблице — два места (contracts/ui-components.md, «Как
   подключать»): проп `widths={settings}` у ResponsiveTable (settings из
   useTableSettings) и TableCols с видимыми столбцами по порядку — первым
   ребёнком таблицы, до thead. ColHead рисует ручку сам.

   МОДЕЛЬ, КОРОТКО. Хранятся только столбцы, которые человек тронул. Пока
   видимых тронутых нет — таблица ровно прежняя (авто-раскладка, ни colgroup,
   ни замеров). Иначе ручной режим = авто-раскладка браузера с ЗАКРЕПЛЁННЫМИ
   тронутыми столбцами, отрисованная через colgroup + `table-layout: fixed`:
     • тронутый столбец — `<col style="width">` из настроек;
     • нетронутый — ровно та ширина, что браузер дал бы ему в авто-раскладке
       этой таблицы на этих данных при таких тронутых (замер — настоящим
       проходом авто-раскладки, где у тронутых `<col>` своя ширина, а у
       нетронутых её нет). Таблица, которой хватает места, так и остаётся в
       контейнере: расширил столбец — нетронутые отдают запас (не уже своего
       содержимого), сузил — забирают освободившееся. Таблица шире контейнера —
       нетронутые как в авто-виде. Ширины рисует сам `<col>` из последней
       раскладки, а пересчитывает их синхронизация — всегда до отрисовки: в
       коммите TableCols, в колбэке наблюдателя за содержимым таблицы
       (микрозадача сразу после коммита — строки мог перерисовать и дочерний
       компонент), на догрузке шрифтов и смене ширины контейнера. Замер — когда
       поменялись тексты ячеек (перестановка строк — сортировка — его не
       требует), набор столбцов, шрифты, ширина контейнера или ЗАФИКСИРОВАНА
       правка ширины (отпускание, шаг с клавиатуры, возврат в авто); замер
       сохраняет прокрутку. Во время самого перетаскивания — ни одного замера:
       меняется только `<col>` тянущегося столбца, соседи встают на место один
       раз, при отпускании;
     • последний `<col>` — служебный, без ширины: он забирает остаток, когда
       отдать место некому (тронуты все видимые столбцы) или таблица сама по
       себе уже контейнера. Тронутый уже своего содержимого освобождает место
       нетронутым (второй проход замера), а не ему. Область служебного столбца
       закрашивается продолжением строк (фон и линии последней ячейки —
       внутренней тенью `<tr>`, под ячейками).

   НАЖАТИЕ — НЕ ПРАВКА. Пока ручку не сдвинули больше чем на DRAG_THRESHOLD px,
   ничего не происходит. Первое настоящее движение снимает раскладку «как
   сейчас» и (из авто-раскладки) рисует colgroup с этими ширинами — тем же
   кадром, без прыжка и без сброса прокрутки вбок; дальше двигается только
   `<col>`. В хранилище — одна запись, на отпускании. Escape, отменённый
   браузером жест (pointercancel) и ушедшая посреди жеста ручка (карточки,
   закрытый отчёт) жест отменяют: ширина и запись — как до него.

   КАРТОЧНЫЙ РЕЖИМ (< 720px контейнера) — ни ручек, ни colgroup: столбцов там
   нет (флаг `narrow` от ResponsiveTable; у таблиц без карточек он свой). */
import React from 'react';
import { flushSync } from 'react-dom';
import {
  MAX_COL_WIDTH, autoSignature, clampWidth, keyboardWidth, leafCells,
  minWidthOf, snapshotWidths, widthPlan,
} from '../../lib/table/columnWidths.js';

const { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } = React;

/* Эффекты, которые обязаны успеть до отрисовки, — layout; на сервере (тесты
   разметки) его нет. */
const useIsoLayoutEffect = typeof window !== 'undefined' && window.document
  ? useLayoutEffect : useEffect;

/* Ближайший кадр (до его отрисовки): то, что не видно глазу или может
   подождать, — значения ручек для скринридера, докраска строки, с которой ушёл
   курсор, синхронизация, отложенная на время перетаскивания. */
const nextFrame = (fn) => (typeof requestAnimationFrame === 'function'
  ? requestAnimationFrame(fn) : setTimeout(fn, 16));
const cancelFrame = (id) => {
  if (!id) return;
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(id);
  else clearTimeout(id);
};

/** Сдвиг ручки, после которого нажатие считается перетаскиванием. */
export const DRAG_THRESHOLD = 3;

/** { settings, narrow, colsRef, autoRef, dragRef, colsApi } — ставит ResponsiveTable (`widths`). */
export const TableWidthsContext = createContext(null);

/** Провайдер для ResponsiveTable (или своей таблицы). Без настроек значение
 *  контекста — null: у неподключённой таблицы ни ручек, ни colgroup. Сам
 *  провайдер стоит ВСЕГДА — иначе смена пропа «есть настройки / нет» меняла бы
 *  тип узла дерева, и React пересоздал бы всё содержимое таблицы. */
export function TableWidthsProvider({ settings, narrow = false, children }) {
  // colsRef — физические столбцы таблицы (пишет TableCols: он стоит в таблице
  // раньше шапки), autoRef — ширины нетронутых и подпись раскладки, dragRef —
  // идёт перетаскивание (замеры заморожены), colsApi — черновая ширина
  // перетаскивания у TableCols (до отпускания в хранилище ничего не пишется).
  const colsRef = useRef(null);
  const autoRef = useRef(null);
  const dragRef = useRef(false);
  const colsApi = useRef(null);
  const value = useMemo(
    () => (settings ? { settings, narrow, colsRef, autoRef, dragRef, colsApi } : null),
    [settings, narrow]);
  return <TableWidthsContext.Provider value={value}>{children}</TableWidthsContext.Provider>;
}

const spanOf = (el) => Math.max(1, Number(el.colSpan) || 1);
// черновик перетаскивания у TableCols (контекст, собранный вручную, может быть без него)
const draftApi = (ctx) => (ctx && ctx.colsApi ? ctx.colsApi.current : null);
const keysOf = (ctx) => (ctx.colsRef.current || []).map((c) => c.key);

/* ── прокрутка ──
   Пока идёт замер, таблица бывает уже, чем была (авто-раскладка без ширин
   тронутых), и браузер обрезает scrollLeft скроллера; без возврата прокрутка
   уезжала к началу, а отсортированный столбец — из вида (замечание
   верификатора, круг 2). Сохраняем позиции скроллера и всех прокручиваемых
   предков (вплоть до документа) и возвращаем их. */
function saveScroll(el) {
  const saved = [];
  for (let n = el ? el.parentElement : null; n; n = n.parentElement) {
    if (n.scrollLeft || n.scrollTop) saved.push([n, n.scrollLeft, n.scrollTop]);
  }
  const root = typeof document !== 'undefined' ? document.scrollingElement : null;
  if (root && (root.scrollLeft || root.scrollTop) && !saved.some(([n]) => n === root)) {
    saved.push([root, root.scrollLeft, root.scrollTop]);
  }
  return saved;
}
// какие из сохранённых позиций браузер успел обрезать (читать, пока раскладка свежая)
const movedScroll = (saved) => saved.filter(([n, left, top]) => n.scrollLeft !== left || n.scrollTop !== top);
const putScroll = (saved) => {
  for (const [n, left, top] of saved) {
    if (n.scrollLeft !== left) n.scrollLeft = left;
    if (n.scrollTop !== top) n.scrollTop = top;
  }
};

/** Сохранить прокрутку вокруг `el` сейчас — вернуть функцией (вокруг правки, которая
 *  может на миг сузить таблицу). */
export function keepScroll(el) {
  const saved = saveScroll(el);
  return () => putScroll(saved);
}

/* Ширина столбца по ячейке тела, где он стоит один (для листа шапки, который
   накрывает несколько столбцов). Первая подходящая строка. */
function bodyCellWidth(table, index) {
  for (const tr of table.querySelectorAll('tbody tr')) {
    let c = 0;
    for (const td of tr.children) {
      const cs = spanOf(td);
      if (c === index && cs === 1) return td.getBoundingClientRect().width;
      if (c > index) break;
      c += cs;
    }
  }
  return 0;
}

/** Ширины столбцов в ТЕКУЩЕЙ раскладке, по порядку `keys` (физические столбцы
 *  таблицы). Источник — листья шапки (многоэтажная шапка разбирается так же,
 *  как её раскладывает браузер); лист на несколько столбцов — тело таблицы. */
export function measureColumns(table, keys) {
  const rows = Array.from(table.querySelectorAll('thead tr')).map((tr) => (
    Array.from(tr.children).map((el) => ({ el, colSpan: spanOf(el), rowSpan: Math.max(1, Number(el.rowSpan) || 1) }))));
  const leaves = leafCells(rows);
  return keys.map((key, i) => {
    const leaf = leaves[i];
    const width = leaf && leaf.colSpan === 1
      ? leaf.el.getBoundingClientRect().width
      : bodyCellWidth(table, i);
    return { key, width };
  });
}

/** Ширины столбцов в АВТО-РАСКЛАДКЕ этой таблицы на этих данных — при ЗАКРЕПЛЁННЫХ
 *  тронутых столбцах (`touched` — {ключ: px}). На миг у colgroup слоя снимается
 *  класс `an-colw` (с ним уходят правила фиксированной раскладки,
 *  `:has(> colgroup.an-colw)`), у нетронутых `<col>` — ширина, у тронутых заголовков —
 *  пометка `rt-th-w`; тронутые `<col>` держат свою ширину. Замер — и всё
 *  возвращается как было, вместе с прокруткой. Синхронно: кадр с промежуточной
 *  раскладкой браузер не рисует; состав таблицы не меняется (наблюдателю за
 *  содержимым нечего замечать).
 *
 *  Почему с закреплёнными тронутыми (круг правок Ф3, второй заход). Ширина
 *  тронутого — ограничение, и авто-раскладка браузера раздаёт остальное вокруг
 *  него: таблица, которой хватает места, остаётся в контейнере (расширили столбец
 *  — нетронутые отдают запас, но не уже своего содержимого; сузили — забирают
 *  освободившееся). Первый заход снимал colgroup целиком и отдавал нетронутым
 *  ширины ЧИСТОЙ авто-раскладки, будто ручных ширин нет: «Ликвидность» после +90
 *  к «Продукту» навсегда становилась шире контейнера на 90 (1468 при 1378) с
 *  прокруткой вбок, хотя у соседей был запас. Таблица шире контейнера ведёт себя
 *  как раньше: нетронутые — как в авто-виде (в тесноте авто-раскладка держит их на
 *  содержимом); тронутый, которого не хватило места удержать, в замере уже своей
 *  ширины — его ширину держит фиксированная раскладка, а таблица выходит шире
 *  контейнера ровно на нехватку. Тронутый уже своего содержимого (замер дал ему
 *  больше) — наоборот: освобождённое им место нетронутые делят вторым проходом
 *  авто-раскладки, пока таблица не дойдёт до края контейнера; служебному столбцу
 *  остаток достаётся, только когда отдать его некому (тронуты все).
 *
 *  Не max-content (первый заход). Таблица шире контейнера в авто-раскладке стоит
 *  между min- и max-content: подписи заголовков переносятся («Поз. / чек» в
 *  «Персонале» — 75,8px, по max-content было бы 106,5). Потолки ячеек отчёта
 *  (`.abc-td-name` 260px) и свои min-width таблиц (`.abc-table` 920px) действуют —
 *  как в авто-раскладке. */
export function measureNatural(table, keys, scrollHint, touched) {
  // scrollHint — известная позиция скроллера ([left, top] из его событий scroll):
  // без неё позиции читаются, а это — лишняя принудительная раскладка до замера
  const sc = table.parentElement;
  const scroll = scrollHint && sc ? [[sc, scrollHint[0], scrollHint[1]]] : saveScroll(table);
  let moved = scroll;
  const found = table.querySelector('colgroup.an-colw');
  const cg = found && found.parentNode === table ? found : null;
  const cols = cg ? Array.from(cg.children || []) : [];
  const saved = cols.map((c) => c.style.width);
  const pin = touched || {};
  const marked = Array.from(table.querySelectorAll('thead th.rt-th-w'));
  if (cg) {
    cg.classList.remove('an-colw');
    cols.forEach((c) => {
      const key = c.dataset ? c.dataset.colKey : null;
      const w = key && pin[key] > 0 ? pin[key] + 'px' : '';
      if (c.style.width !== w) c.style.width = w;
    });
  }
  marked.forEach((th) => th.classList.remove('rt-th-w'));
  try {
    const out = {};
    for (const { key, width } of measureColumns(table, keys)) out[key] = width;
    // Тронутый, которому авто-раскладка дала БОЛЬШЕ его ширины (он уже своего
    // содержимого — числа и подписи без переносов, как в ABC), освобождает место
    // только в фиксированной раскладке. Это место — тоже нетронутым («сузил —
    // остальные забирают освободившееся»), но не больше, чем нужно таблице до края
    // контейнера: второй проход той же авто-раскладки с таблицей шире на это место —
    // нетронутые делят его по правилам браузера. Без него сужение такого столбца
    // оставляло справа пустую область служебного столбца.
    let freed = 0;
    for (const k of keys) if (pin[k] > 0 && out[k] > pin[k]) freed += out[k] - pin[k];
    if (cg && sc && freed > 0.5) {
      const tw = table.getBoundingClientRect().width;
      const room = Math.min(freed, sc.clientWidth - (tw - freed));
      if (room > 0.5) {
        const inline = table.style.width;
        table.style.width = (tw + room) + 'px';
        try {
          for (const { key, width } of measureColumns(table, keys)) if (!(pin[key] > 0)) out[key] = width;
        } finally {
          table.style.width = inline;
        }
      }
    }
    // раскладка замера свежая — какие позиции браузер обрезал, видно без новой
    // раскладки; возвращать — только их (обычно — ничего, и лишней раскладки нет)
    moved = movedScroll(scroll);
    return out;
  } finally {
    if (cg) {
      cols.forEach((c, i) => { if (c.style.width !== saved[i]) c.style.width = saved[i]; });
      cg.classList.add('an-colw');
    }
    marked.forEach((th) => th.classList.add('rt-th-w'));
    putScroll(moved);
  }
}

/** Подпись СОДЕРЖИМОГО таблицы: тексты ячеек по столбцам как мультимножество.
 *  Перестановка строк (сортировка, «Показать ещё» по тем же строкам — нет)
 *  её не меняет, а замер по содержимому дорог — две раскладки всей таблицы.
 *  Совпала с прошлой — ширины по содержимому те же, замер не нужен. */
export function contentSignature(table) {
  const byCol = new Map();
  for (const tr of table.querySelectorAll('thead > tr, tbody > tr, tfoot > tr')) {
    let c = 0;
    for (const cell of tr.children) {
      const span = spanOf(cell);
      const k = span === 1 ? String(c) : c + '+' + span;
      let list = byCol.get(k);
      if (!list) { list = []; byCol.set(k, list); }
      list.push(cell.textContent);
      c += span;
    }
  }
  return Array.from(byCol.keys()).sort()
    .map((k) => k + ':' + byCol.get(k).sort().join('\u0001'))
    .join('\u0002');
}

/* ── продолжение строк в области служебного столбца ──
   У строк там нет ячеек, и браузер рисует пустоту: обрывались полоса шапки,
   разделители, фон «Итого» и групп. Строка получает фон и верхнюю/нижнюю линии
   своей ПОСЛЕДНЕЙ ячейки — одним правилом для всех видов строк любого отчёта
   (группы, итоги, хвост окна, «минусовые» остатки), без списка классов. Линия
   снизу — только если последняя ячейка не уходит на следующие ряды (rowSpan).

   Рисуется внутренней тенью `<tr>` (под ячейками) — инлайн-свойством box-shadow
   самой строки. Не CSS-переменными: смена переменной на строке заставляет
   браузер заново считать стили всех её ячеек (замер круга 2: 60–80 мс на 200
   строк против 2 мс у box-shadow).

   Строку тела ПОД КУРСОРОМ не читаем: её фон сейчас — подсветка наведения, и он
   «запекался» бы в строку до следующего замера (замечание верификатора, круг 2).
   Она ждёт ухода курсора (paintPending), а пока подсвечена — продолжение рисует
   CSS-правило наведения (линия строки для него — переменная таблицы
   `--an-rest-line`, одна на таблицу). */
const isClear = (c) => !c || c === 'transparent' || /rgba\([^)]*,\s*0\)$/.test(c);
const isBodyRow = (tr) => !!tr.parentElement && tr.parentElement.tagName === 'TBODY';
const isHovered = (tr) => {
  try { return !!(tr.matches && tr.matches(':hover')); } catch { return false; }
};

function readRow(tr) {
  const last = tr.lastElementChild;
  if (!last) return null;
  const cs = getComputedStyle(last);
  const bg = isClear(cs.backgroundColor) ? null : cs.backgroundColor;
  const tw = parseFloat(cs.borderTopWidth) || 0;
  const bw = parseFloat(cs.borderBottomWidth) || 0;
  const ownRow = (Number(last.rowSpan) || 1) === 1;
  const top = tw > 0 && cs.borderTopStyle !== 'none' && !isClear(cs.borderTopColor)
    ? 'inset 0 ' + tw + 'px 0 ' + cs.borderTopColor : null;
  const bottom = ownRow && bw > 0 && cs.borderBottomStyle !== 'none' && !isClear(cs.borderBottomColor)
    ? 'inset 0 -' + bw + 'px 0 ' + cs.borderBottomColor : null;
  const shadow = [top, bottom, bg ? 'inset 0 0 0 9999px ' + bg : null].filter(Boolean).join(', ');
  return { bg, top, bottom, shadow };
}

/* Писать только изменившееся (сортировка переставляет строки — значения те же).
   Обычной строке тела (без фона и верхней линии, с общей нижней линией)
   инлайн-тень не нужна: её линию рисует CSS из переменной таблицы
   `--an-rest-line` — так на вход в ручной режим строки тела почти не трогаются. */
function writeRow(tr, v, line) {
  if (!v) return;
  const plain = isBodyRow(tr) && !v.bg && !v.top && (v.bottom || '') === (line || '');
  const shadow = plain ? '' : v.shadow;
  if ((tr.style.boxShadow || '') !== shadow) {
    if (shadow) tr.style.boxShadow = shadow;
    else tr.style.removeProperty('box-shadow');
  }
  if (v.bg) { if (!tr.hasAttribute('data-an-rest')) tr.setAttribute('data-an-rest', ''); } else if (tr.hasAttribute('data-an-rest')) tr.removeAttribute('data-an-rest');
}

/* Сначала ВСЕ чтения, потом записи: чтение после записи заставляло браузер
   пересчитывать стили на каждой строке. Линия обычной строки тела — с первой
   такой строки, переменной таблицы (её же берёт правило наведения). */
function paintRows(table, rows) {
  const vals = rows.map(readRow);
  let line = table ? table.style.getPropertyValue('--an-rest-line') : '';
  const i = rows.findIndex((tr, k) => isBodyRow(tr) && vals[k] && !vals[k].bg && !vals[k].top);
  if (table && i >= 0) {
    const next = vals[i].bottom || '';
    if (line !== next) {
      if (next) table.style.setProperty('--an-rest-line', next);
      else table.style.removeProperty('--an-rest-line');
      line = next;
    }
  }
  rows.forEach((tr, k) => writeRow(tr, vals[k], line));
}

/** Продолжение всех строк таблицы. `pending` — сюда откладываются строки тела под
 *  курсором (без него — читаются все, как есть). */
export function paintRest(table, pending) {
  if (typeof getComputedStyle !== 'function') return;
  const rows = [];
  for (const tr of table.querySelectorAll('thead > tr, tbody > tr, tfoot > tr')) {
    if (pending && isBodyRow(tr) && isHovered(tr)) { pending.add(tr); continue; }
    if (pending) pending.delete(tr);
    rows.push(tr);
  }
  paintRows(table, rows);
}

/** Докрасить отложенные строки таблицы `table`, с которых ушёл курсор. */
export function paintPending(pending, table) {
  if (!pending || !pending.size || typeof getComputedStyle !== 'function') return;
  const rows = [];
  for (const tr of Array.from(pending)) {
    if (!tr.isConnected) { pending.delete(tr); continue; }
    if (isHovered(tr)) continue;
    pending.delete(tr);
    rows.push(tr);
  }
  if (rows.length) paintRows(table, rows);
}

export function clearRest(table) {
  if (!table) return;
  table.style.removeProperty('--an-rest-line');
  for (const tr of table.querySelectorAll('thead > tr, tbody > tr, tfoot > tr')) {
    if (tr.style.boxShadow) tr.style.removeProperty('box-shadow');
    tr.removeAttribute('data-an-rest');
  }
}

function colEl(table, key) {
  return Array.from(table.querySelectorAll('colgroup.an-colw > col'))
    .find((c) => c.dataset && c.dataset.colKey === key) || null;
}

/* Ширины нетронутых в DOM — тем же значением, что потом нарисует TableCols из
   autoRef: перерисовка без смены данных их не трогает. */
function applyAuto(table, untouched, widths) {
  const free = new Set(untouched);
  for (const c of table.querySelectorAll('colgroup.an-colw > col')) {
    const key = c.dataset && c.dataset.colKey;
    if (!key || !free.has(key)) continue;
    const w = widths[key];
    const v = w > 0 ? w + 'px' : '';
    if (c.style.width !== v) c.style.width = v;
  }
}

/** Синхронизация ручного режима — до отрисовки (коммит, колбэк наблюдателя,
 *  шрифты, ResizeObserver); записи наблюдателя забирает первая же синхронизация,
 *  так что изменения одного коммита — одна синхронизация.
 *  Замер авто-раскладки (с закреплёнными тронутыми) — только когда без него
 *  нельзя: первый раз, сменился набор или порядок столбцов, ЗАФИКСИРОВАНА правка
 *  ширины (набор тронутых ширин другой: отпускание, клавиша, возврат в авто;
 *  первая правка из авто-раскладки — тоже, `adopt`), догрузились шрифты,
 *  поменялись ТЕКСТЫ ячеек (подпись содержимого; сортировка её не меняет) или
 *  ширина контейнера (авто-раскладка от неё зависит: свободное место и переносы
 *  подписей). Нетронутые получают ровно ширины этого замера; остаток — служебному
 *  `<col>`. Во время перетаскивания — ничего: соседи встают на место один раз, на
 *  отпускании. */
export function syncTable(ctx, ob) {
  const { table } = ob;
  if (!ob.alive || !ob.plan || ctx.dragRef.current) return;
  if (ob.mo && ob.mo.takeRecords && ob.mo.takeRecords().length) ob.records = true;
  const keys = ob.cols.map((c) => c.key);
  const keysSig = keys.join('\u0001');
  const touched = ob.plan.touched || {};
  const touchedSig = keys.map((k) => (touched[k] > 0 ? k + '=' + touched[k] : '')).join('\u0001');
  // ширина контейнера — из ResizeObserver (без чтения раскладки посреди кадра)
  const available = ob.available != null ? ob.available : (table.parentElement ? table.parentElement.clientWidth : 0);
  let st = ctx.autoRef.current || { widths: {} };
  const text = ob.records || ob.text == null ? contentSignature(table) : ob.text;
  const measure = !ob.natural || ob.keysSig !== keysSig || ob.touchedSig !== touchedSig
    || ob.fontsDirty || !!st.adopt || text !== ob.text || ob.measuredAt !== available;
  if (measure) {
    ob.natural = measureNatural(table, keys, ob.scroll, touched);
    ob.keysSig = keysSig;
    ob.touchedSig = touchedSig;
    ob.fontsDirty = false;
    ob.measuredAt = available;
    ob.measures += 1;
  }
  ob.text = text;
  ob.available = available;
  const natural = ob.natural;
  const sig = autoSignature(keys, natural, available);
  // снимок «как сейчас» первой правки (`adopt`) держал нетронутые, пока шёл жест;
  // после фиксации их ширины — из замера
  if (st.adopt || st.sig !== sig) st = { widths: {}, sig };
  // нетронутые — ровно по авто-раскладке при закреплённых тронутых (и столбец,
  // который только что вернули в авто двойным кликом, тоже)
  const widths = { ...(st.widths || {}) };
  for (const k of ob.plan.untouched) if (!(widths[k] > 0) && natural[k] > 0) widths[k] = natural[k];
  ctx.autoRef.current = { ...st, widths };
  applyAuto(table, ob.plan.untouched, widths);
  if (ob.records || !ob.painted) {
    paintRest(table, ob.pending);
    ob.painted = true;
  } else {
    paintPending(ob.pending, ob.table);
  }
  ob.records = false;
}

const noopObserver = { observe() {}, disconnect() {}, takeRecords: () => [] };

/* Наблюдатель за содержимым таблицы. Колбэк СИНХРОНИЗИРУЕТ (микрозадача сразу
   после коммита, до кадра): строки может перерисовать дочерний компонент со
   своим состоянием, а TableCols при этом не перерисуется — без колбэка замера
   не было бы (замечание верификатора, круг 2). Атрибуты не смотрим: их меняем
   мы сами (ширины, продолжение строк, подписи ручек), а строк и текста
   синхронизация не трогает — так что циклов нет. */
function observeTable(table, onChange) {
  if (typeof MutationObserver === 'undefined') return noopObserver;
  const mo = new MutationObserver(() => onChange());
  mo.observe(table, { childList: true, characterData: true, subtree: true });
  return mo;
}

/* Чего наблюдатель не видит, а ширину по содержимому меняет или раздачу места
   требует:
   • догрузка веб-шрифта — цифры в Geist Mono шире запасного шрифта;
   • ширина контейнера без перерисовки отчёта (окно растянули) — синхронизация
     прямо в колбэке ResizeObserver (он идёт после раскладки, до отрисовки). */
function watchOutside(ob, ctx) {
  const fonts = typeof document !== 'undefined' ? document.fonts : null;
  const onFonts = () => { ob.fontsDirty = true; syncTable(ctx, ob); };
  if (fonts && fonts.addEventListener) fonts.addEventListener('loadingdone', onFonts);
  if (fonts && fonts.status === 'loading' && fonts.ready) fonts.ready.then(() => { if (ob.alive) onFonts(); });
  let ro = null;
  const scroller = ob.table.parentElement;
  if (scroller && typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(() => {
      if (!ob.alive) return;
      const w = scroller.clientWidth;
      if (w === ob.available) return;
      const known = ob.available != null;
      ob.available = w;
      if (known && ob.plan) syncTable(ctx, ob);
    });
    ro.observe(scroller);
  }
  // позиция прокрутки скроллера — из его событий: замеру не нужно её читать
  const onScroll = () => { ob.scroll = [scroller.scrollLeft, scroller.scrollTop]; };
  if (scroller && scroller.addEventListener) scroller.addEventListener('scroll', onScroll, { passive: true });
  // курсор ушёл со строки, которую не докрасили (была под ним), — докрасить
  const onOut = () => {
    if (!ob.pending.size || ob.outFrame) return;
    ob.outFrame = nextFrame(() => { ob.outFrame = 0; if (ob.alive) paintPending(ob.pending, ob.table); });
  };
  if (ob.table.addEventListener) ob.table.addEventListener('mouseout', onOut);
  return () => {
    if (fonts && fonts.removeEventListener) fonts.removeEventListener('loadingdone', onFonts);
    if (ro) ro.disconnect();
    if (scroller && scroller.removeEventListener) scroller.removeEventListener('scroll', onScroll);
    if (ob.table.removeEventListener) ob.table.removeEventListener('mouseout', onOut);
    cancelFrame(ob.outFrame);
  };
}

function createObserver(ctx, table) {
  const ob = {
    table, alive: true, frame: 0, outFrame: 0, records: false, painted: false, fontsDirty: false,
    natural: null, keysSig: null, text: null, available: null, measures: 0, pending: new Set(),
    cols: [], plan: null,
  };
  // в ближайший кадр — после перетаскивания (во время него синхронизация молчит)
  ob.schedule = () => {
    if (ob.frame || !ob.alive) return;
    ob.frame = nextFrame(() => { ob.frame = 0; syncTable(ctx, ob); });
  };
  ob.mo = observeTable(table, () => { ob.records = true; syncTable(ctx, ob); });
  const sc = table.parentElement;
  ob.scroll = sc && sc.scrollLeft != null ? [sc.scrollLeft || 0, sc.scrollTop || 0] : null;
  ob.stop = watchOutside(ob, ctx);
  return ob;
}

function dropObserver(ob) {
  ob.alive = false;
  cancelFrame(ob.frame);
  ob.mo.disconnect();
  if (ob.stop) ob.stop();
  ob.pending.clear();
  clearRest(ob.table);
}

/** `<colgroup>` таблицы — ставится ПЕРВЫМ ребёнком `<table>`. cols — физические
 *  столбцы по порядку показа (тот же список, что рисуют шапка и строки).
 *  Рисуется, только когда хоть один видимый столбец тронут (или идёт первое
 *  перетаскивание — черновая ширина). Нетронутые `<col>` получают ширины
 *  последней раскладки (autoRef) прямо в разметке: в коммите первой правки это
 *  снимок «как сейчас», и таблица не сжимается ни на миг (иначе браузер обрезал
 *  прокрутку вбок). Таблицу находит через СВОЙ colgroup, а не через ref обёртки:
 *  на первом монтировании layout-эффект ребёнка идёт раньше, чем к обёртке
 *  прикреплён ref. */
export function TableCols({ cols }) {
  const ctx = useContext(TableWidthsContext);
  const [draft, setDraft] = useState(null);
  const cgRef = useRef(null);
  const obsRef = useRef(null);
  const list = cols || [];
  if (ctx) {
    ctx.colsRef.current = list;
    if (ctx.colsApi) ctx.colsApi.current = { setDraft, schedule: () => { if (obsRef.current) obsRef.current.schedule(); } };
  }
  const live = !!ctx && !ctx.narrow;
  const stored = live && draft ? { ...ctx.settings.widths, [draft.key]: draft.width } : (live ? ctx.settings.widths : null);
  const plan = live ? widthPlan(stored, list.map((c) => c.key)) : null;
  const manual = !!plan && plan.manual;
  const auto = manual && ctx.autoRef.current ? ctx.autoRef.current.widths || {} : {};

  useIsoLayoutEffect(() => {
    const cg = cgRef.current;
    const table = manual && cg ? cg.parentElement : null;
    let ob = obsRef.current;
    if (ob && ob.table !== table) {
      dropObserver(ob);
      obsRef.current = null;
      ob = null;
    }
    if (!table) {
      // вышли из ручного режима: следующий вход снимет раскладку заново
      if (ctx && !ctx.dragRef.current) ctx.autoRef.current = null;
      return;
    }
    if (!ob) {
      ob = createObserver(ctx, table);
      obsRef.current = ob;
    }
    ob.cols = list;
    ob.plan = plan;
    syncTable(ctx, ob);
  });
  useIsoLayoutEffect(() => () => {
    if (obsRef.current) dropObserver(obsRef.current);
    obsRef.current = null;
  }, []);

  if (!manual) return null;
  return (
    <colgroup ref={cgRef} className="an-colw">
      {list.map((c) => {
        const w = plan.touched[c.key] != null ? plan.touched[c.key] : auto[c.key];
        return <col key={c.key} data-col-key={c.key} style={w > 0 ? { width: w + 'px' } : undefined} />;
      })}
      <col className="an-colw__rest" />
    </colgroup>
  );
}

function readableName(settings, col) {
  const own = (settings.columns || []).find((c) => c.key === col.key);
  const src = own && own.label ? own : col;
  return String(src.label || col.key) + (src.label2 ? ' ' + src.label2 : '');
}

/** Наименьшая ширина столбца ПО ЕГО ЗАГОЛОВКУ: поля ячейки, стрелки сортировки и
 *  воронка фильтра — плюс место под «…». Уже этого подпись резалась посреди
 *  буквы, а многоточию не оставалось места («Группа» в 56px читалась как «I ⇕»).
 *  Считается по детям `.rt-th` (а не по ширине заголовка: у числовых и широких
 *  столбцов `.rt-th` шире своего содержимого). Считать — у заголовка с классом
 *  тронутого столбца (`rt-th-w`): подпись тогда в одну строку и с обрезкой.
 *
 *  Свой `<th>` без `.rt-th` (заголовок «Товар / группа» матрицы «Инвентаризации»)
 *  — по его самому широкому неразрывному ребёнку (кнопка сортировки с подписью и
 *  т. п.): подпись внутри него так же режется до «…»; переносимый текст (блок с
 *  обычным переносом) и сама ручка минимума не задают. Раньше здесь был 0, и
 *  такой заголовок сжимался до общего минимума посреди подписи. */
export function headMinWidth(th) {
  if (!th || !th.querySelector || typeof getComputedStyle !== 'function') return 0;
  const px = (v) => parseFloat(v) || 0;
  const rt = th.querySelector('.rt-th');
  const cs = getComputedStyle(th);
  const box = px(cs.paddingLeft) + px(cs.paddingRight) + px(cs.borderLeftWidth) + px(cs.borderRightWidth);
  const ellipsisOf = (label) => px(getComputedStyle(label).fontSize) || 12;   // «…» ≈ 0,8em — с запасом
  if (rt) {
    const label = rt.querySelector ? rt.querySelector('.rt-th__label') : null;
    if (!label) return 0;
    const kids = Array.from(rt.children || []);
    const inner = kids.reduce((s, el) => s + el.getBoundingClientRect().width, 0)
      + px(getComputedStyle(rt).columnGap) * Math.max(0, kids.length - 1);
    return Math.ceil(box + inner - label.getBoundingClientRect().width + ellipsisOf(label));
  }
  let inner = 0;
  for (const el of Array.from(th.children || [])) {
    if (el.classList && el.classList.contains('an-colresize')) continue;
    const ecs = getComputedStyle(el);
    if (ecs.position === 'absolute' || ecs.position === 'fixed' || ecs.display === 'none') continue;
    // переносимый текст (блок с обычным переносом) сжимается сам — минимума не задаёт
    if ((ecs.display === 'block' || ecs.display === 'list-item') && ecs.whiteSpace !== 'nowrap') continue;
    const label = el.querySelector ? el.querySelector('.rt-th__label') : null;
    const w = el.getBoundingClientRect().width
      - (label ? label.getBoundingClientRect().width - ellipsisOf(label) : 0);
    inner = Math.max(inner, w);
  }
  return inner > 0 ? Math.ceil(box + inner) : 0;
}

/* Склонение — как fmt.plural (lib/format.js при импорте требует window, а слой
   таблиц грузится и в тестах разметки без него). */
const pixels = (n) => {
  const m10 = n % 10; const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'пиксель';
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return 'пикселя';
  return 'пикселей';
};

/** Значение separator и пометка заголовка — по фактической раскладке. Класс
 *  тронутого столбца ставится ДО замера минимума (иначе у букв ABC выходило 52
 *  при фактических 64 — подпись без класса не режется). */
export function markHead(el, { touched, name, colTitle }) {
  const th = el && el.closest ? el.closest('th') : null;
  if (!th) return null;
  th.classList.toggle('rt-th-w', !!touched);
  const lbl = th.querySelector('.rt-th__label');
  if (lbl && !colTitle) {
    if (touched) lbl.setAttribute('title', name);
    else lbl.removeAttribute('title');
  }
  return th;
}

export function updateHandle(el, { touched, min, name, colTitle }) {
  const th = markHead(el, { touched, name, colTitle });
  if (!th) return;
  const w = Math.round(th.getBoundingClientRect().width);
  el.setAttribute('aria-valuemin', String(Math.max(min, headMinWidth(th))));
  el.setAttribute('aria-valuenow', String(w));
  el.setAttribute('aria-valuetext', w + ' ' + pixels(w) + (touched ? '' : ', по содержимому'));
}

/* На время перетаскивания: класс страницы (курсор границы везде, без выделения
   текста) и Escape на документе — ручку мышью не фокусируем: фокус ушёл бы от
   поля, где человек только что печатал, и на ручке зажглась бы рамка фокуса. */
const docDrag = (on, onKey) => {
  if (typeof document === 'undefined') return;
  if (document.documentElement) document.documentElement.classList[on ? 'add' : 'remove']('is-col-resizing');
  if (onKey && document.addEventListener) {
    if (on) document.addEventListener('keydown', onKey, true);
    else document.removeEventListener('keydown', onKey, true);
  }
};

/* Снять следы перетаскивания со страницы и с ручки (без записи). Синхронизация,
   отложенная на время жеста (замеры заморожены), — в ближайший кадр. */
function stopDrag(ctx, d) {
  try { d.el.releasePointerCapture(d.id); } catch { /* захвата уже нет */ }
  if (d.el.classList) d.el.classList.remove('is-dragging');
  if (d.active) docDrag(false, d.onKey);
  if (!ctx) return;
  ctx.dragRef.current = false;
  const api = draftApi(ctx);
  if (d.active && api && api.schedule) api.schedule();
}

/* Отмена жеста — ровно как было до него, и в таблице, и в хранилище (в него во
   время жеста ничего не писалось). Первая правка из авто-раскладки — черновик
   снимается, таблица возвращается в авто-раскладку; иначе `<col>` получает
   прежнюю ширину. Прокрутка вбок — как до жеста. */
function revertDrag(ctx, d, { sync = true } = {}) {
  if (!d.active) return;
  // столбец до жеста не был тронут — и пометки тронутого заголовка у него нет
  if (d.prev == null && d.th && d.th.classList) d.th.classList.remove('rt-th-w');
  if (d.draft) {
    const api = draftApi(ctx);
    if (api) {
      if (sync) flushSync(() => { api.setDraft(null); });
      else api.setDraft(null);
    }
    if (!ctx.dragRef.current) ctx.autoRef.current = null;
  } else {
    const c = colEl(d.table, d.key);
    if (c) c.style.width = d.before > 0 ? d.before + 'px' : '';
  }
  if (d.restoreScroll) d.restoreScroll();
}

/** Ручка ширины на правой границе заголовка. Её рисует ColHead; в свой `<th>`
 *  (не ColHead) — `<ColResize col={{ key, label }} />`. `col.resizable === false`
 *  — ручки нет. Свой минимум — `col.minWidth` (столбец с кнопкой в ячейке). */
export function ColResize({ col, label }) {
  const ctx = useContext(TableWidthsContext);
  const drag = useRef(null);
  const elRef = useRef(null);
  const physical = ctx && ctx.colsRef.current;
  const visible = !!ctx && !ctx.narrow && !!col && col.resizable !== false
    && !!physical && physical.some((c) => c.key === col.key);
  const key = col ? col.key : '';
  const own = visible ? (ctx.settings.columns || []).find((c) => c.key === key) : null;
  const min = Math.max(minWidthOf(own), minWidthOf(col));
  const touched = visible && ctx.settings.widths[key] != null;
  const name = visible ? (label || readableName(ctx.settings, col)) : '';
  const colTitle = col ? col.title : undefined;

  /* Пометка тронутого столбца в заголовке (подпись в одну строку с многоточием)
     — сразу, в коммите: её видно. Значение separator (всегда — фактическая
     ширина, в авто — замеренная) — в кадре: его читает только скринридер, а
     чтение раскладки посреди коммита дорого. */
  useIsoLayoutEffect(() => {
    const el = elRef.current;
    if (!visible || !el) return undefined;
    if (!(drag.current && drag.current.active)) markHead(el, { touched, name, colTitle });
    const id = nextFrame(() => {
      if (drag.current && drag.current.active) return;
      updateHandle(el, { touched, min, name, colTitle });
    });
    return () => cancelFrame(id);
  });

  /* Ручка исчезла посреди перетаскивания (таблица ушла в карточки, отчёт
     закрыт): жест отменяется — снять класс страницы и слушатель Escape, ширину и
     запись оставить как до жеста (в хранилище во время жеста не писалось). */
  useEffect(() => () => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    stopDrag(ctx, d);
    revertDrag(ctx, d, { sync: false });
  }, [visible]);

  if (!visible) return null;

  const cancel = () => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    stopDrag(ctx, d);
    revertDrag(ctx, d);
  };

  // Отпустили: одна запись — если ширина поменялась (черновик снимается тем же
  // коммитом); вернули границу на место — как будто жеста не было.
  const finish = () => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    stopDrag(ctx, d);
    if (!d.active) return;
    if (d.w === d.start) { revertDrag(ctx, d, { sync: false }); return; }
    ctx.settings.setWidth(d.key, d.w, { min: d.min });
    const api = draftApi(ctx);
    if (d.draft && api) api.setDraft(null);
  };

  // Первое настоящее движение: снять раскладку «как сейчас». Из авто-раскладки —
  // черновой colgroup с этими же ширинами (тем же кадром, прокрутка на месте).
  const activate = (d) => {
    const measured = measureColumns(d.table, keysOf(ctx));
    const cur = measured.find((m) => m.key === key);
    d.th = d.el.closest ? d.el.closest('th') : null;
    if (d.th && d.th.classList) d.th.classList.add('rt-th-w');     // минимум — у тронутого заголовка
    d.min = Math.max(min, headMinWidth(d.th));
    d.prev = ctx.settings.widths[key] != null ? ctx.settings.widths[key] : null;
    d.start = clampWidth(cur && cur.width > 0 ? cur.width : (d.prev || d.min), { min: d.min });
    d.w = d.start;
    d.active = true;
    d.onKey = (ke) => { if (ke.key === 'Escape') { ke.preventDefault(); cancel(); } };
    if (d.el.classList) d.el.classList.add('is-dragging');
    const manual = !!d.table.querySelector('colgroup.an-colw');
    d.restoreScroll = keepScroll(d.table);
    if (manual) {
      const c = colEl(d.table, key);
      d.before = c ? parseFloat(c.style.width) || 0 : 0;
      if (c) c.style.width = d.start + 'px';
    } else {
      d.draft = true;
      ctx.autoRef.current = { widths: snapshotWidths(measured), adopt: true };
      ctx.dragRef.current = true;                      // замеры заморожены уже в коммите черновика
      const api = draftApi(ctx);
      // синхронно: следующее движение уже найдёт `<col>` этого столбца
      if (api) flushSync(() => { api.setDraft({ key, width: d.start }); });
      d.restoreScroll();
    }
    ctx.dragRef.current = true;
    docDrag(true, d.onKey);
  };

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    // Без записи и без смены раскладки: нажатие — ещё не правка. preventDefault —
    // не начинать выделение текста страницы и не отдавать нажатие заголовку.
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget;
    const table = el.closest ? el.closest('table') : null;
    if (!table) return;
    drag.current = { x0: e.clientX, id: e.pointerId, el, table, key, min, active: false };
    try { el.setPointerCapture(e.pointerId); } catch { /* нет поддержки — тянем без захвата */ }
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x0;
    if (!d.active) {
      if (Math.abs(dx) <= DRAG_THRESHOLD) return;
      activate(d);
    }
    const w = clampWidth(d.start + dx, { min: d.min });
    if (w == null || w === d.w) return;
    d.w = w;
    const c = colEl(d.table, key);
    if (c) c.style.width = w + 'px';
    d.el.setAttribute('aria-valuenow', String(Math.round(w)));
  };

  // Двойной клик / Enter: вернуть ЭТОТ столбец в авто (убрать из сохранённых).
  const resetToAuto = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (ctx.settings.widths[key] != null) ctx.settings.clearWidth(key);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape' && drag.current && drag.current.active) { e.preventDefault(); cancel(); return; }
    if (e.key === 'Enter') { resetToAuto(e); return; }
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget;
    const table = el.closest ? el.closest('table') : null;
    if (!table) return;
    const th = el.closest('th');
    if (th && th.classList) th.classList.add('rt-th-w');            // минимум — у тронутого заголовка
    const kmin = Math.max(min, headMinWidth(th));
    const stored = ctx.settings.widths[key];
    let cur = stored;
    let first = false;
    if (cur == null) {
      const measured = measureColumns(table, keysOf(ctx));
      const m = measured.find((x) => x.key === key);
      cur = m && m.width > 0 ? clampWidth(m.width, { min: kmin }) : null;
      if (!table.querySelector('colgroup.an-colw')) {
        first = true;
        ctx.autoRef.current = { widths: snapshotWidths(measured), adopt: true };
      }
    }
    const next = cur == null ? null : keyboardWidth(cur, e.key, { shift: e.shiftKey, min: kmin });
    if (next == null || next === stored) {
      if (stored == null && th && th.classList) th.classList.remove('rt-th-w');
      return;
    }
    if (first) {
      // первая правка из авто-раскладки: снимок «как сейчас» в той же разметке, прокрутка на месте
      const restoreScroll = keepScroll(table);
      flushSync(() => { ctx.settings.setWidth(key, next, { min: kmin }); });
      restoreScroll();
    } else {
      ctx.settings.setWidth(key, next, { min: kmin });
    }
  };

  return (
    <span ref={elRef} className="an-colresize" role="separator" aria-orientation="vertical" tabIndex={0}
      aria-label={'Ширина столбца «' + name + '»'}
      aria-valuemin={min} aria-valuemax={MAX_COL_WIDTH}
      title="Ширина столбца: потяните границу. Двойной клик — снова по содержимому"
      onPointerDown={onPointerDown} onPointerMove={onPointerMove}
      onPointerUp={finish} onPointerCancel={cancel} onLostPointerCapture={finish}
      onDoubleClick={resetToAuto} onKeyDown={onKeyDown}
      onClick={(e) => e.stopPropagation()} />
  );
}
